import { useState } from "react";
import PropTypes from "prop-types";

import TableSkeletonLoader from "../../../../utils/TableSkeletonLoader";
import defaultImage from "../../../../utils/DefaultImage";
import DashboardTooltip from "../../Components/DashboardTooltip";
import ImagePreviewModal from "../../Components/ImagePreviewModal";

const ProposalOrderWineList = ({
    wines = [],
    financialSummary,
    isLoading = false,
}) => {
    // Image modal state
    const [selectedImage, setSelectedImage] = useState(null);

    // Helper to sanitize image URL
    const cleanImageUrl = (url) => {
        if (!url || url === "null") return null;
        let clean = url;
        if (clean.startsWith("[")) {
            clean = clean.split('"]')[0].replace('["', "");
        }
        return clean;
    };

    return (
        <>
            <div className="w-full dash-table-main h-auto bg-[#FBFBFB] border-[2px] border-[#8B24332B] rounded-xl shadow-xs overflow-hidden overflow-x-auto mb-4">
                <table className="md:w-full min-w-[700px] text-left wine_table">
                    <thead>
                        <tr className="border-b-2 border-[#8B2433]/30 bg-[#FAF3F4]">
                            <th className="py-3.5 px-3 first:pl-6 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">
                                Sr.no
                            </th>
                            <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">
                                Immagine
                            </th>
                            <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">
                                Nome Vino
                            </th>
                            <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">
                                Prezzo (B2B)
                            </th>
                            <th className="py-3.5 px-3 text-center poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">
                                Quantità
                            </th>
                            <th className="py-3.5 px-3 last:pr-6 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">
                                Prezzo Totale
                            </th>
                        </tr>
                    </thead>

                    {isLoading ? (
                        <TableSkeletonLoader
                            columns={6}
                            rows={5}
                            minWidths={["60px", "80px", "220px", "110px", "90px", "110px"]}
                            actionCount={0}
                        />
                    ) : (
                        <tbody>
                            {wines && wines.length > 0 ? (
                                wines.map((item, index) => {
                                    const wineImage = cleanImageUrl(item?.wine?.image) || defaultImage;

                                    return (
                                        <tr
                                            key={item?.item_id || index}
                                            className="border-b border-[#8B2433]/12 hover:bg-[#8B2433]/[0.03] transition-colors duration-150"
                                        >
                                            {/* 1. Sr.no */}
                                            <td className="py-4 px-3 first:pl-6 text-left poppins-medium text-[#303030]">
                                                {index + 1}
                                            </td>

                                            {/* 2. Immagine */}
                                            <td className="pr-2 py-4 px-3">
                                                <div
                                                    className="thumbnail_image rounded-lg w-[50px] h-[60px] bg-white p-1 flex items-center justify-center cursor-pointer hover:shadow-md transition-shadow"
                                                    onClick={() => setSelectedImage(wineImage)}
                                                >
                                                    <img
                                                        className="w-full p-1 h-full object-contain border border-[#68172394] rounded-lg"
                                                        src={wineImage}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = defaultImage;
                                                        }}
                                                        alt={item?.wine?.name || "Wine"}
                                                    />
                                                </div>
                                            </td>

                                            {/* 3. Nome Vino */}
                                            <td className="py-4 px-3 text-left poppins-medium text-[#303030] text-sm">
                                                <DashboardTooltip
                                                    text={item?.wine?.name || "N/A"}
                                                    maxLength={35}
                                                />
                                            </td>

                                            {/* 4. Prezzo (B2B) */}
                                            <td className="py-4 px-3 text-left poppins-medium text-[#303030] whitespace-nowrap font-medium text-sm">
                                                {item?.unit_price_net_formatted || "0,00 €"}
                                            </td>

                                            {/* 5. Quantità */}
                                            <td className="py-4 px-3 text-center poppins-medium whitespace-nowrap">
                                                <span className="inline-flex items-center justify-center w-[38px] h-[38px] rounded-full bg-[#F0ECED] text-[#8B2433] text-sm font-semibold shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                                                    {item.quantity ?? 0}
                                                </span>
                                            </td>

                                            {/* 6. Prezzo Totale */}
                                            <td className="py-4 px-3 last:pr-6 text-left poppins-medium text-[#303030] whitespace-nowrap font-medium text-sm">
                                                {item?.line_total_net_formatted || "0,00 €"}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={6} className="text-center py-6 text-gray-500 poppins-regular">
                                        Nessun vino trovato nell&apos;ordine
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    )}
                </table>
            </div>

            {/* Bottom Total Order Summary Bar (Square / Rounded-xl Card Style) */}
            {!isLoading && wines && wines.length > 0 && (
                <div className="w-full mb-6 py-3 px-5 sm:px-6 bg-gradient-to-r from-[#8B2433] via-[#7B1E2B] to-[#651520] text-white rounded-xl flex flex-wrap justify-between items-center gap-3 shadow-sm border border-[#8B2433]/30 transition-all">
                    {/* Left Side: Section Label */}
                    <div className="flex items-center gap-2.5">
                        <span className="inline-block w-2 h-2 rounded-full bg-white/70"></span>
                        <span className="text-white/90 text-[13px] sm:text-sm poppins-semibold tracking-wide uppercase">
                            Riepilogo Ordine
                        </span>
                    </div>

                    {/* Right Side: Totals Chips */}
                    <div className="flex items-center gap-3 sm:gap-4 ml-auto">
                        {/* Pezzi Chip */}
                        <div className="inline-flex items-center gap-2 bg-black/20 border border-white/15 px-3.5 sm:px-4 py-1.5 rounded-lg">
                            <span className="text-white/80 text-xs sm:text-sm poppins-medium whitespace-nowrap">
                                Pezzi:
                            </span>
                            <span className="text-white text-xs sm:text-sm poppins-bold">
                                {financialSummary?.total_bottles ?? 0}
                            </span>
                        </div>

                        {/* Totale Ordine Chip */}
                        <div className="inline-flex items-center gap-2.5 bg-white/15 border border-white/25 px-4 sm:px-5 py-1.5 rounded-lg shadow-xs">
                            <span className="text-white/90 text-xs sm:text-sm poppins-medium whitespace-nowrap">
                                Totale ordine:
                            </span>
                            <span className="text-white text-sm sm:text-base poppins-bold tracking-tight whitespace-nowrap">
                                {financialSummary?.subtotal_net_formatted || "0,00 €"}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Common Image Preview Modal */}
            <ImagePreviewModal
                imageUrl={selectedImage}
                onClose={() => setSelectedImage(null)}
            />
        </>
    );
};

ProposalOrderWineList.propTypes = {
    wines: PropTypes.arrayOf(PropTypes.object),
    financialSummary: PropTypes.object,
    isLoading: PropTypes.bool,
};

export default ProposalOrderWineList;
